import { Typography, Box, TableSortLabel, TablePagination, Button, CircularProgress, IconButton, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Dialog, DialogTitle, DialogContent, DialogActions, Theme } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import axios from "axios";
import ReportSkeleton from "components/common/ReportSkeleton";
import React, { Suspense, useEffect, useState } from "react";
import { joinUrl } from "lib/utils";
import { createSecurityHeaders } from "services/securityApi";
import { useNavigate } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useSidebar } from "context/SidebarContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:7001";

interface Pentest {
  attack_paths_count: number | null;
  aws_account_ids: string[];
  canceled_at: string | null;
  client_name: string;
  completed_at: string | null;
  credentials_count: number;
  data_resources_count: number | null;
  duration_s: number | null;
  etl_completed_at: string | null;
  exclude_scope: string[];
  external_domains_count: number;
  hosts_count: number;
  impacts_count: number;
  launched_at: string | null;
  max_scope: string[];
  min_scope: string[];
  name: string;
  nodezero_script_url: string | null;
  op_id: string;
  out_of_scope_hosts_count: number;
  runner: {
    pid: string;
    uid: string;
  } | null;
  scheduled_at: string;
  services_count: number;
  state: string;
  user_name: string;
  users_count: number;
  weaknesses_count: number;
  websites_count: number;
}

const HorizonViewDetails = () => {
  const [pentests, setPentests] = useState<Pentest[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedPentest, setSelectedPentest] = useState<Pentest | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const navigate = useNavigate();
  const { isOpen } = useSidebar();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [orderBy, setOrderBy] = useState('name');
  const [order, setOrder] = useState<'asc' | 'desc'>('asc');
  const [totalPentests, setTotalPentests] = useState(0);

  useEffect(() => {
    setLoading(true);
    const fetchData = async () => {
      try {
        const headers = createSecurityHeaders();
        if (headers) {
          const params = {
            page: page + 1,
            pageSize: rowsPerPage,
            orderBy: orderBy,
            sortOrder: order.toUpperCase(),
          };
          const horizonResponse = await axios.get(joinUrl(BACKEND_URL, "/horizon3ai/pentests"), { headers, params });
          setPentests(horizonResponse.data.pentests_page.pentests);
          setTotalPentests(horizonResponse.data.pentests_page.page_info.total || 0);
        }
      } catch (error) {
        console.error("Error fetching pentests:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [page, rowsPerPage, order, orderBy]);

  const handleRowClick = (pentest: Pentest) => {
    setSelectedPentest(pentest);
    setIsDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setSelectedPentest(null);
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleRequestSort = (event: React.MouseEvent<unknown>, property: string) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const createSortHandler = (property: string) => (event: React.MouseEvent<unknown>) => {
    handleRequestSort(event, property);
  };

  return (
    <Suspense fallback={<ReportSkeleton />}>
      <Box sx={(theme: Theme) => ({
        backgroundColor: theme.palette.mode === 'dark' ? 'rgba(17, 17, 17, 0.6)' : '#fdfdfd',
        height: '100%',
        pt: '40px',
        color: theme.palette.mode === 'dark' ? '#FFFFFF' : 'inherit'
      })}>
        <Box sx={(theme: Theme) => {
          return {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            position: 'fixed',
            top: 0,
            left: isOpen ? '240px' : '68px',
            right: 0,
            zIndex: 1000,
            backgroundColor: theme.palette.mode === 'dark' ? 'rgba(17, 17, 17, 0.6)' : '#FFFFFF',
            backdropFilter: 'blur(8px)',
            padding: '15px 24px',
          };
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <IconButton
              onClick={() => navigate(-1)}
              size="large"
              sx={(theme: Theme) => ({
                color: theme.palette.mode === 'dark' ? '#FFFFFF' : '#1E293B'
              })}
            >
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="h4" sx={{ color: 'primary.main', fontWeight: 'bold' }}>
              Horizon Pentests
            </Typography>
          </Box>
        </Box>

        <Box sx={{ mt: 10, px: 3 }}>
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sortDirection={orderBy === 'name' ? order : false}>
                      <TableSortLabel
                        active={orderBy === 'name'}
                        direction={orderBy === 'name' ? order : 'asc'}
                        onClick={createSortHandler('name')}
                      >
                        Name
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>Client Name</TableCell>
                    <TableCell sortDirection={orderBy === 'state' ? order : false}>
                      <TableSortLabel
                        active={orderBy === 'state'}
                        direction={orderBy === 'state' ? order : 'asc'}
                        onClick={createSortHandler('state')}
                      >
                        State
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>User Name</TableCell>
                    <TableCell sortDirection={orderBy === 'completed_at' ? order : false}>
                       <TableSortLabel
                        active={orderBy === 'completed_at'}
                        direction={orderBy === 'completed_at' ? order : 'asc'}
                        onClick={createSortHandler('completed_at')}
                      >
                        Completed At
                      </TableSortLabel>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pentests.length > 0 ? (
                    pentests.map((pentest: Pentest, i) => (
                      <TableRow key={i} onClick={() => handleRowClick(pentest)} style={{ cursor: 'pointer' }}>
                        <TableCell>{pentest.name || "Untitled Pentest"}</TableCell>
                        <TableCell>{pentest.client_name}</TableCell>
                        <TableCell>{pentest.state}</TableCell>
                        <TableCell>{pentest.user_name}</TableCell>
                        <TableCell>{pentest.completed_at ? new Date(pentest.completed_at).toLocaleString() : "N/A"}</TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={5} align="center">
                        No pentests found.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
              <TablePagination
                rowsPerPageOptions={[5, 10, 25]}
                component="div"
                count={totalPentests}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            </TableContainer>
          )}
        </Box>
        {selectedPentest && (
          <Dialog
            open={isDialogOpen}
            onClose={handleCloseDialog}
            maxWidth={false}
            fullWidth
            PaperProps={{
              sx: {
                borderRadius: '8px',
                maxHeight: '92vh',
                backgroundColor: (theme: Theme) => theme.palette.mode === 'dark' ? '#111111' : 'white',
                margin: '24px',
                width: '80%',
              }
            }}
          >
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 2, backgroundColor: (theme: Theme) => theme.palette.mode === 'dark' ? '#111111' : 'white' }}>
              <Typography variant="h6" sx={{ fontWeight: 600, color: (theme: Theme) => theme.palette.mode === 'dark' ? '#F1F5F9' : '#374151' }}>
                Pentest Details
              </Typography>
              <IconButton onClick={handleCloseDialog} size="small" sx={{ color: (theme: Theme) => theme.palette.mode === 'dark' ? '#94A3B8' : '#6B7280' }}>
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            <DialogContent sx={{ backgroundColor: (theme: Theme) => theme.palette.mode === 'dark' ? '#111111' : 'white', pt: 0 }}>
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 3, mb: 3, p: 2, backgroundColor: (theme: Theme) => theme.palette.mode === 'dark' ? 'rgba(26, 26, 26, 0.8)' : '#F9FAFB', borderRadius: '8px' }}>
                <Box>
                  <Typography variant="body2" sx={{ color: (theme: Theme) => theme.palette.mode === 'dark' ? '#94A3B8' : '#6B7280', mb: 1 }}>Op ID</Typography>
                  <Typography variant="body1" sx={{ color: (theme: Theme) => theme.palette.mode === 'dark' ? '#F3F4F6' : '#111827' }}>{selectedPentest.op_id}</Typography>
                </Box>
                <Box>
                  <Typography variant="body2" sx={{ color: (theme: Theme) => theme.palette.mode === 'dark' ? '#94A3B8' : '#6B7280', mb: 1 }}>Scheduled At</Typography>
                  <Typography variant="body1" sx={{ color: (theme: Theme) => theme.palette.mode === 'dark' ? '#F3F4F6' : '#111827' }}>{new Date(selectedPentest.scheduled_at).toLocaleString()}</Typography>
                </Box>
                <Box>
                  <Typography variant="body2" sx={{ color: (theme: Theme) => theme.palette.mode === 'dark' ? '#94A3B8' : '#6B7280', mb: 1 }}>Duration (s)</Typography>
                  <Typography variant="body1" sx={{ color: (theme: Theme) => theme.palette.mode === 'dark' ? '#F3F4F6' : '#111827' }}>{selectedPentest.duration_s ?? "N/A"}</Typography>
                </Box>
                <Box>
                  <Typography variant="body2" sx={{ color: (theme: Theme) => theme.palette.mode === 'dark' ? '#94A3B8' : '#6B7280', mb: 1 }}>Hosts Count</Typography>
                  <Typography variant="body1" sx={{ color: (theme: Theme) => theme.palette.mode === 'dark' ? '#F3F4F6' : '#111827' }}>{selectedPentest.hosts_count}</Typography>
                </Box>
                <Box>
                  <Typography variant="body2" sx={{ color: (theme: Theme) => theme.palette.mode === 'dark' ? '#94A3B8' : '#6B7280', mb: 1 }}>Services Count</Typography>
                  <Typography variant="body1" sx={{ color: (theme: Theme) => theme.palette.mode === 'dark' ? '#F3F4F6' : '#111827' }}>{selectedPentest.services_count}</Typography>
                </Box>
                <Box>
                  <Typography variant="body2" sx={{ color: (theme: Theme) => theme.palette.mode === 'dark' ? '#94A3B8' : '#6B7280', mb: 1 }}>Weaknesses Count</Typography>
                  <Typography variant="body1" sx={{ color: (theme: Theme) => theme.palette.mode === 'dark' ? '#F3F4F6' : '#111827' }}>{selectedPentest.weaknesses_count}</Typography>
                </Box>
                <Box>
                  <Typography variant="body2" sx={{ color: (theme: Theme) => theme.palette.mode === 'dark' ? '#94A3B8' : '#6B7280', mb: 1 }}>Credentials Count</Typography>
                  <Typography variant="body1" sx={{ color: (theme: Theme) => theme.palette.mode === 'dark' ? '#F3F4F6' : '#111827' }}>{selectedPentest.credentials_count}</Typography>
                </Box>
                <Box>
                  <Typography variant="body2" sx={{ color: (theme: Theme) => theme.palette.mode === 'dark' ? '#94A3B8' : '#6B7280', mb: 1 }}>Users Count</Typography>
                  <Typography variant="body1" sx={{ color: (theme: Theme) => theme.palette.mode === 'dark' ? '#F3F4F6' : '#111827' }}>{selectedPentest.users_count}</Typography>
                </Box>
              </Box>
            </DialogContent>
            <DialogActions sx={{ backgroundColor: (theme: Theme) => theme.palette.mode === 'dark' ? '#111111' : 'white' }}>
              <Button onClick={handleCloseDialog}>Close</Button>
            </DialogActions>
          </Dialog>
        )}
      </Box>
    </Suspense>
  );
};

export default HorizonViewDetails;
